import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import {
  useAuth,
  useTenantBillingOverview,
  useTenantBootstrap,
} from '@tark/shared/hooks';
import type { TenantAthleteBalanceSummary } from '@tark/shared/types';
import { ArrowRight, BookOpenCheck, CreditCard, Loader2, PackageCheck, ReceiptText, Users, XCircle } from 'lucide-react';
import CoachPageHeader from '../components/CoachPageHeader';
import CoachProgramCatalogPanel from './schedule/program-catalog/CoachProgramCatalogPanel';

const MANAGE_ROLES = ['OWNER', 'ADMIN', 'COACH', 'STAFF'];

export default function CoachPaymentsPage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const navigate = useNavigate();
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const canManage = MANAGE_ROLES.includes(activeTenant?.roleCode?.toUpperCase() || '');
  const billing = useTenantBillingOverview(Boolean(user) && isCoachStudio && canManage);

  const athletesNeedingAction = useMemo(
    () =>
      (billing.data?.athletes ?? [])
        .filter(
          (athlete) =>
            (athlete.unpaidBookingCount || 0) > 0 ||
            (athlete.lowCreditPackageCount || 0) > 0 ||
            (athlete.expiringPackageCount || 0) > 0
        )
        .slice(0, 8),
    [billing.data?.athletes]
  );

  if (!isCoachStudio || !canManage) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">Payments need a Coach Studio</h1>
              <p className="mt-2 text-sm text-text-muted">
                Switch into an enabled coach workspace to review package credits and manual payment status.
              </p>
            </div>
            <Button onClick={() => navigate('/welcome')}>Open Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (billing.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (billing.isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-text">Payments unavailable</h1>
              <p className="mt-2 text-sm text-text-muted">Refresh and we will restore package and payment balances.</p>
            </div>
            <Button variant="outline" onClick={() => billing.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = billing.data?.summary;

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <CoachPageHeader
        title="Payments"
        subtitle="Record class payments, reconcile balances, and review package credits from one money workspace."
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {activeTenant?.tenant?.displayName || 'Coach Studio'}
          </span>
        }
        action={
          <Button leftIcon={<Users className="h-4 w-4" />} onClick={() => navigate('/directory')}>
            Open Students
          </Button>
        }
        stats={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MiniStat label="Unpaid" value={summary?.unpaidBookingCount ?? 0} />
            <MiniStat label="Credits" value={formatCredits(summary?.totalCreditsRemaining)} />
            <MiniStat label="Low" value={summary?.lowCreditPackageCount ?? 0} />
            <MiniStat label="Expiring" value={summary?.expiringPackageCount ?? 0} />
          </div>
        }
      />

      <Card variant="default">
        <CardContent className="space-y-1">
          <h2 className="text-base font-semibold text-text">Outside payment</h2>
          <p className="text-sm text-text-muted">Card payments in TARK aren't available yet. Use pay outside TARK.</p>
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ReceiptText className="h-5 w-5 text-primary" />
            Needs payment attention
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {athletesNeedingAction.length > 0 ? (
            athletesNeedingAction.map((athlete) => (
              <AthletePaymentRow key={athlete.athleteId} athlete={athlete} onOpen={() => navigate(`/directory?athleteId=${athlete.athleteId}`)} />
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-surface-elevated/20 px-4 py-8 text-center">
              <p className="font-semibold text-text">No payment attention needed</p>
              <p className="mt-1 text-sm text-text-muted">Package credits and unpaid booking counts will appear here when there is activity.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageCheck className="h-5 w-5 text-primary" />
            Package plans
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {(billing.data?.packagePlans ?? []).length > 0 ? (
            billing.data?.packagePlans.map((plan) => (
              <div key={plan.packagePlanId} className="rounded-xl border border-border bg-surface px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-text">{plan.planName}</p>
                    <p className="mt-1 text-xs text-text-muted">
                      {plan.sessionCreditCount} credits
                      {plan.priceAmount != null ? ` • ${formatMoney(plan.priceAmount, plan.currencyCode)}` : ''}
                    </p>
                  </div>
                  <span className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-semibold text-text-muted">
                    {plan.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-surface-elevated/20 px-4 py-8 text-center md:col-span-2">
              <p className="font-semibold text-text">No package plans yet</p>
              <p className="mt-1 text-sm text-text-muted">Create packages from a student profile when you are ready to track credits.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <BookOpenCheck className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold tracking-tight text-text">Class registration payments</h2>
        </div>
        <CoachProgramCatalogPanel
          enabled={Boolean(user) && isCoachStudio && canManage}
          canManageSchedule={canManage}
          mode="payments"
        />
      </div>

      {billing.data?.securityNotice ? (
        <p className="text-xs leading-5 text-text-subtle">{billing.data.securityNotice}</p>
      ) : null}
    </div>
  );
}

function AthletePaymentRow({ athlete, onOpen }: { athlete: TenantAthleteBalanceSummary; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-surface-elevated"
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-text">{athlete.displayName}</span>
        <span className="mt-1 block truncate text-xs text-text-muted">
          {formatCredits(athlete.creditsRemaining)} credits
          {athlete.unpaidBookingCount > 0 ? ` • ${athlete.unpaidBookingCount} unpaid` : ''}
          {athlete.lowCreditPackageCount > 0 ? ` • ${athlete.lowCreditPackageCount} low` : ''}
          {athlete.expiringPackageCount > 0 ? ` • ${athlete.expiringPackageCount} expiring` : ''}
        </span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-text-subtle" />
    </button>
  );
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold leading-none text-text">{value}</p>
    </div>
  );
}

function formatCredits(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) {
    return '0';
  }

  const rounded = Math.round(Number(value) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatMoney(amount?: number | null, currency: string = 'USD'): string {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '$0';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
  }).format(Number(amount));
}
